// The chrome behind the season summary (ui-map-interface.md §3.4, R1519).
//
// When the season is over, the map and the summary are the whole screen.
// "The season is over" means the store holds a season summary for the
// world and season on the glass (`state.summary`), in every mode, on a
// world that rolls and on a world that the calendar holds.
//
// - On a page load or a rejoin while a summary stands, the chrome does not
//   arrive at all: the veil hides it at once, and the page settles the
//   dock-in with no travel (`fast/main.ts`).
// - In a running session, the chrome fades out over `chrome_fade_ms` when
//   the summary arrives. Reduced motion hides it at once.
// - A hello that empties the summary (a roll, a reseed, a new world) takes
//   the veil off, and the chrome comes back with its normal arrival. A
//   hello of the same world and season (a reconnect) empties it too, and
//   the hub sends it again right after the hello, so the veil holds for
//   `veil_hold_ms` before it gives the chrome back. The hold has an end, so
//   the chrome can never stay hidden with no summary behind it.
//
// The veil is a class on `#ui` and on the minimap canvas, and the
// stylesheet states what it hides. The veil also makes every hidden child
// of `#ui` `inert`, so no press and no focus reaches it. The card, the dev
// panel, the dev segment of the top bar and `#fatal` stay. The one control
// the veil adds is SEASON SUMMARY on the map, which opens the card again
// after VIEW MAP put it away.
//
// **The top bar comes back on the map** (R2094, ui-map-interface.md §3.4).
// While the card is open, the card states the clock. After VIEW MAP the
// card is gone, so `#ui` wears `seasonmap` and the stylesheet gives the
// top bar back, with its timer claim NEW SEASON counting to the roll. The
// bar fades in over the veil's own fade and holds no control: the chrome's
// actions do nothing while the veil stands.
//
// `veilStep` is the rule and holds no DOM. `ChromeVeil` applies it.
//
// **Photo mode is the veil's second cause** (ui-map-interface.md §13.1,
// R1623). `setPhoto(true)` raises the veil with a second class,
// `photomode`, under which the stylesheet hides the card, the dev panel
// and the whole top bar as well: the slim photo bar and `#fatal` alone
// stay. The two causes stand together: photo mode over a season summary
// hides the summary, and leaving it gives the summary back.

import { SEASON_SUMMARY_CHROME_FADE_MS, SEASON_SUMMARY_VEIL_HOLD_MS } from '../gen/presentation';
import { seasonSummary as S } from '../gen/strings';
import { reducedMotion } from '../render/motion';
import type { GameState } from '../state';
import { PressRoute } from './pressroute';

/**
 * Where the veil is.
 *
 * - `off` — the chrome stands as usual.
 * - `fading` — the chrome fades out and takes no press.
 * - `hidden` — the chrome is gone; the map and the summary remain.
 */
export type VeilPhase = 'off' | 'fading' | 'hidden';

/**
 * The next phase of the veil. `over` says a summary stands. `arriving`
 * says the chrome has not arrived yet (a page load, a rejoin), so there
 * is nothing on the glass to fade. A phase that already veils stays.
 */
export function veilStep(
  phase: VeilPhase, over: boolean, arriving: boolean, reduced: boolean,
): VeilPhase {
  if (!over) return 'off';
  if (phase !== 'off') return phase;
  return arriving || reduced ? 'hidden' : 'fading';
}

/** The class the veil puts on its hosts. The stylesheet keys off it. */
export const VEIL_CLASS = 'seasonover';
/** The class photo mode adds beside `VEIL_CLASS` (R1623). */
export const PHOTO_CLASS = 'photomode';
/** The class `#ui` wears while a summary stands and the card is put away:
 *  the stylesheet shows the top bar again (R2094). */
export const MAP_CLASS = 'seasonmap';
/** The class that stops every transition for one reflow, so an instant
 *  hide does not fade. */
const INSTANT_CLASS = 'veilnow';

/** The children of `#ui` that the veil leaves alone: the card, its reopen
 *  button, the dev panel, the readout line (the stylesheet hides its
 *  figures; it holds no control since R1749) and the fatal stop. */
const KEEP = new Set(['seasonsummary', 'ssreopen', 'admin', 'topbar', 'fatal']);
/** The children of `#ui` photo mode leaves alone: its bar, its two lines
 *  and the fatal stop. */
const PHOTO_KEEP = new Set(['photobar', 'photohint', 'photosaved', 'fatal']);

export interface ChromeVeilDeps {
  /** The chrome root `#ui`. */
  shell: HTMLElement;
  state: GameState;
  /** True while the chrome has not arrived yet (the dock-in is not idle). */
  arriving: () => boolean;
  /** Other hosts that wear the class: the minimap canvas. */
  also?: () => (HTMLElement | null)[];
  reduced?: () => boolean;
  fadeMs?: number;
  holdMs?: number;
}

export class ChromeVeil {
  private phaseNow: VeilPhase = 'off';
  private timer = 0;
  /** The world and season the veil went up for, and the hold that waits
   *  for their summary after a hello of the same world and season. */
  private veiledKey = '';
  private holdTimer = 0;
  private holdOver = false;
  /** True while photo mode is on (R1623). */
  private photo = false;
  private readonly reopen: HTMLButtonElement;
  private readonly inerted: HTMLElement[] = [];

  constructor(private readonly d: ChromeVeilDeps) {
    const b = document.createElement('button');
    b.id = 'ssreopen';
    b.type = 'button';
    b.textContent = S.btn_open;
    b.hidden = true;
    // Fixed chrome over the map: it acts on the press, and Enter and Space
    // act through their click (§16.8, R1206, R1569). The button stands for
    // the life of the page, so the router is never taken down.
    new PressRoute(b, { control: 'button', scrolls: () => false, act: () => this.d.state.showSummary(true) });
    d.shell.appendChild(b);
    this.reopen = b;
    d.state.on(() => this.sync());
    this.sync();
  }

  /** Where the veil is; the tests read it. */
  get phase(): VeilPhase { return this.phaseNow; }

  /** True while the chrome is veiled or on its way out: the chrome's keys
   *  do nothing, and the page plays no arrival. */
  get active(): boolean { return this.phaseNow !== 'off'; }

  /** True once the fade has ended: the minimap closes. */
  get hidden(): boolean { return this.phaseNow === 'hidden'; }

  /** True while photo mode is a cause of the veil. */
  get photoOn(): boolean { return this.photo; }

  /**
   * Photo mode on or off (R1623). The veil goes up for it as it goes up
   * for a summary, and the stylesheet hides the rest of the chrome under
   * `photomode`. The children that may stay change with the cause, so the
   * inert marks are set again from nothing.
   */
  setPhoto(on: boolean): void {
    if (on === this.photo) return;
    this.photo = on;
    this.hosts((h) => h.classList.toggle(PHOTO_CLASS, on));
    for (const el of this.inerted.splice(0)) el.inert = false;
    this.sync();
  }

  /** Follows the store: a summary arrived, stands, or went. */
  sync(): void {
    const s = this.d.state;
    const key = `${s.hello?.worldId ?? 0}:${s.hello?.season ?? 0}`;
    let over = s.summary !== null;
    if (over) {
      this.veiledKey = key;
      this.endHold();
    } else if (this.phaseNow !== 'off' && key === this.veiledKey && !this.holdOver) {
      // A hello of the same world and season: the summary comes again.
      over = true;
      if (!this.holdTimer) {
        this.holdTimer = window.setTimeout(() => {
          this.holdTimer = 0;
          this.holdOver = true;
          this.sync();
        }, this.d.holdMs ?? SEASON_SUMMARY_VEIL_HOLD_MS);
      }
    }
    const reduced = (this.d.reduced ?? reducedMotion)();
    const next = veilStep(this.phaseNow, over || this.photo, this.d.arriving(), reduced);
    if (next !== this.phaseNow) this.enter(next);
    else if (next !== 'off') this.markInert();
    this.reopen.hidden = !this.active || this.photo || s.summaryShown;
    this.d.shell.classList.toggle(MAP_CLASS,
      this.active && !this.photo && s.summary !== null && !s.summaryShown);
  }

  private enter(next: VeilPhase): void {
    const was = this.phaseNow;
    this.phaseNow = next;
    window.clearTimeout(this.timer);
    this.timer = 0;
    if (next === 'off') {
      this.veiledKey = '';
      this.endHold();
      this.hosts((h) => h.classList.remove(VEIL_CLASS));
      for (const el of this.inerted.splice(0)) el.inert = false;
      return;
    }
    if (was === 'off') {
      if (next === 'hidden') {
        // No fade: the class lands with every transition stopped.
        this.hosts((h) => {
          h.classList.add(INSTANT_CLASS, VEIL_CLASS);
          void h.offsetHeight;
          h.classList.remove(INSTANT_CLASS);
        });
      } else {
        this.hosts((h) => h.classList.add(VEIL_CLASS));
        this.timer = window.setTimeout(() => {
          this.timer = 0;
          if (this.phaseNow === 'fading') this.phaseNow = 'hidden';
        }, this.d.fadeMs ?? SEASON_SUMMARY_CHROME_FADE_MS);
      }
    }
    this.markInert();
  }

  /** Makes every child of `#ui` the veil hides inert. It runs on each
   *  change of the store while the veil stands, because a panel can join
   *  the shell after the veil went up (an unlock card, the marker menu). */
  private markInert(): void {
    const keep = this.photo ? PHOTO_KEEP : KEEP;
    for (const el of Array.from(this.d.shell.children)) {
      if (!(el instanceof HTMLElement) || keep.has(el.id) || el.inert) continue;
      el.inert = true;
      this.inerted.push(el);
    }
  }

  private endHold(): void {
    window.clearTimeout(this.holdTimer);
    this.holdTimer = 0;
    this.holdOver = false;
  }

  /** Runs `fn` on every host. Each host states the fade itself, because
   *  the minimap canvas is not inside `#ui`. */
  private hosts(fn: (h: HTMLElement) => void): void {
    const fade = `${this.d.fadeMs ?? SEASON_SUMMARY_CHROME_FADE_MS}ms`;
    for (const h of [this.d.shell, ...(this.d.also?.() ?? [])]) {
      if (!h) continue;
      h.style.setProperty('--veil-fade', fade);
      fn(h);
    }
  }
}
